/**
 * Contact Service
 * Handles contact operations with Aetheris Backend
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError, map, shareReplay } from 'rxjs';
import { APP_SCHEMA_ENDPOINTS, buildUrlWithParams } from '../api';
import { ContactRead, ContactUpdate, ContactCompleteDetails } from '../models';

@Injectable({
  providedIn: 'root',
})
export class ContactService {
  private readonly http = inject(HttpClient);
  private readonly contactCache = new Map<string, Observable<any>>();

  // Signals for reactive state management
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly currentContact = signal<ContactRead | null>(null);

  /**
   * Get contact by ID
   */
  getContact(contactId: number): Observable<ContactRead> {
    this.loading.set(true);
    this.error.set(null);

    return this.http
      .get<ContactRead>(APP_SCHEMA_ENDPOINTS.contacts.getById(contactId), {
        headers: this.createHeaders(),
      })
      .pipe(
        tap((contact) => {
          this.currentContact.set(contact);
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error))
      );
  }

  /**
   * Get contact by email address
   * ✅ With request deduplication to prevent duplicate API calls
   */
  getContactByEmail(email: string): Observable<ContactRead> {
    const cacheKey = `email:${email}`;

    // ✅ Return in-flight request if already loading
    if (this.contactCache.has(cacheKey)) {
      return this.contactCache.get(cacheKey)!;
    }

    this.loading.set(true);
    this.error.set(null);

    // Use specialized by-email endpoint
    const request$ = this.http
      .get<ContactRead>(
        `${APP_SCHEMA_ENDPOINTS.contacts.list}by-email/${encodeURIComponent(email)}`,
        {
          headers: this.createHeaders(),
        }
      )
      .pipe(
        tap((contact) => {
          this.currentContact.set(contact);
          this.loading.set(false);
          // ✅ Remove from cache after successful completion
          this.contactCache.delete(cacheKey);
        }),
        shareReplay(1),
        catchError((error) => {
          this.contactCache.delete(cacheKey);
          return this.handleError(error);
        })
      );

    this.contactCache.set(cacheKey, request$);
    return request$;
  }

  /**
   * Get complete contact details (all related data in one call)
   * Returns: { contact, addresses, mailphones, categories, modules, degrees }
   * ✅ With request deduplication to prevent duplicate API calls
   */
  getContactComplete(contactId: number): Observable<ContactCompleteDetails> {
    const cacheKey = `complete:${contactId}`;

    // ✅ Return in-flight request if already loading
    if (this.contactCache.has(cacheKey)) {
      return this.contactCache.get(cacheKey)!;
    }

    this.loading.set(true);
    this.error.set(null);

    // Use the same pattern as webapps-sis-frontend: /app/contacts/{id}/complete
    const url = `${APP_SCHEMA_ENDPOINTS.contacts.list}${contactId}/complete`;

    const request$ = this.http
      .get<ContactCompleteDetails>(url, {
        headers: this.createHeaders(),
      })
      .pipe(
        tap((completeData) => {
          this.loading.set(false);
          // ✅ Remove from cache after successful completion
          this.contactCache.delete(cacheKey);
        }),
        shareReplay(1),
        catchError((error) => {
          this.contactCache.delete(cacheKey);
          return this.handleError(error);
        })
      );

    this.contactCache.set(cacheKey, request$);
    return request$;
  }

  /**
   * Update contact
   */
  updateContact(contactId: number, data: ContactUpdate): Observable<ContactRead> {
    this.loading.set(true);
    this.error.set(null);

    return this.http
      .put<ContactRead>(APP_SCHEMA_ENDPOINTS.contacts.update(contactId), data, {
        headers: this.createHeaders(),
      })
      .pipe(
        tap((contact) => {
          this.currentContact.set(contact);
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error))
      );
  }

  /**
   * Clear ToCheck flags for contact data
   * Called after user has updated missing data
   */
  clearToCheckFlags(contactId: number, dataType: 'address' | 'phone' | 'all'): Observable<any> {
    const url = `${APP_SCHEMA_ENDPOINTS.contacts.list}${contactId}/clear-tocheck?data_type=${dataType}`;

    return this.http
      .post<any>(
        url,
        {},
        {
          headers: this.createHeaders(),
        }
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Clear current contact
   */
  clearContact(): void {
    this.currentContact.set(null);
    this.error.set(null);
  }

  /**
   * Create HTTP headers
   */
  private createHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  /**
   * Handle errors
   */
  private handleError(error: unknown): Observable<never> {
    this.loading.set(false);
    const errorMessage = error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten';
    this.error.set(errorMessage);
    console.error('❌ ContactService Error:', error);
    return throwError(() => error);
  }
}
